<template>
  <div class="container" ref="wrap">
    <slot></slot>
    <div
      class="popover"
      v-show="show"
      ref="content"
      :style="popoverStyle"
      oncontextmenu="return false"
      :class="popoverClass"
    >
      <slot name="content">{{ content }}</slot>
    </div>
  </div>
</template>

<script>
const eventMap = {
  contextmenu: "contextmenu",
  click: "click",
  hover: "mouseover",
};
export default {
  components: {},
  props: {
    trigger: {
      type: String,
      default: "contextmenu",
      validator: (value) => ["contextmenu", "click", "hover", "manual"].includes(value),
    },
    popoverClass: String,
    content: String,
    placement: {
      type: String,
      default: "bottom",
      validator: (value) =>
        [
          "top",
          "top-start",
          "top-end",
          "bottom",
          "bottom-start",
          "bottom-end",
          "left",
          "left-start",
          "left-end",
          "right",
          "right-start",
          "right-end",
        ].includes(value),
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      popoverStyle: {},
      show: false,
      w: 0,
      h: 0,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler (visible) {
        if (this.trigger === 'contextmenu') return
        if(visible) {
          this.$nextTick(() => this.handleEvent())
        } else {
          this.show = false
        }
      }
    }
  },
  computed: {
    eventName() {
      return eventMap[this.trigger];
    },
    hiddenEvent() {
      if (this.trigger === "hover") {
        return "mousemove";
      }
      return "click";
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.content);

    this.trigger !== 'manual' && document.body.addEventListener(this.hiddenEvent, this.handleHiddenEvent);

    this.trigger !== 'manual' && this.$refs.wrap.firstChild.addEventListener(this.eventName, this.handleEvent)

    this.w =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;

    this.h =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      document.body.clientHeight;

    if (this.trigger !== "contextmenu") {
      this.watchScroll();
    }
  },
  destroyed() {
    if (this.trigger !== 'manual') {
      document.body.removeEventListener(this.hiddenEvent, this.handleHiddenEvent);
    }

    if (this.$refs.wrap && this.$refs.wrap.firstChild) {
      if (this.trigger !== 'manual') {
        this.$refs.wrap.firstChild.removeEventListener(this.eventName, this.handleEvent);
      }
    }

    if (this.$refs.content && this.$refs.content.parentNode === document.body) {
      document.body.removeChild(this.$refs.content);
    }
  },
  methods: {
    async handleEvent(event) {
      this.show = true;
      if (this.trigger === "contextmenu") {
        this.popoverStyle = await this.getContextmenuStyle(event);
      } else {
        this.popoverStyle = await this.getHoverOrClickStyle();
      }
    },
    getInnerSize() {
      let wrapDom = this.$refs.wrap;
      let innerDom = wrapDom.firstChild;
      return innerDom.getBoundingClientRect();
    },
    handleHiddenEvent(event) {
      let target = event.target;
      if (
        ["click", "hover"].includes(this.trigger) &&
        !this.$refs.content.contains(target) &&
        !this.$refs.wrap.firstChild.contains(target)
      ) {
        this.show = false;
        this.$emit('input', false)
      } else if (
        ["contextmenu"].includes(this.trigger) &&
        !this.$refs.content.contains(target)
      ) {
        this.show = false;
      }
    },
    async getContextmenuStyle({ clientX, clientY }) {
      let contentDom = this.$refs.content;
      let width = 0;
      let height = 0;
      await this.$nextTick();
      width = contentDom.offsetWidth;
      height = contentDom.offsetHeight;
      let style = {};
      if (clientX + width > this.w) {
        style.left = Number(clientX - width) + "px";
      } else {
        style.left = clientX + "px";
      }
      if (clientY + height > this.h) {
        style.bottom = 0 + "px";
      } else {
        style.top = clientY + "px";
      }
      return style;
    },
    async getHoverOrClickStyle() {
      let { height, top, left, width } = this.getInnerSize();

      let contentDom = this.$refs.content;
      let contentHeight = 0;
      let contentWidth = 0;

      await this.$nextTick(); // Wait for v-show to render content
      contentHeight = contentDom.offsetHeight;
      contentWidth = contentDom.offsetWidth;

      let vPlacement = 'center'; // vertical placement
      let hPlacement = 'center'; // horizontal placement

      if (this.placement.startsWith('bottom')) vPlacement = 'bottom';
      if (this.placement.startsWith('top')) vPlacement = 'top';
      if (this.placement.startsWith('left')) hPlacement = 'left';
      if (this.placement.startsWith('right')) hPlacement = 'right';

      const variant = this.placement.endsWith('-start') ? 'start' :
                      this.placement.endsWith('-end') ? 'end' : 'center';

      let calcTop, calcLeft;

      const topForBottom = top + height;
      const topForTop = top - contentHeight;
      const topForCenter = top + height / 2 - contentHeight / 2; // Aligns centers

      if (vPlacement === 'bottom') {
        calcTop = topForBottom;
        if (calcTop + contentHeight > this.h && topForTop > 0) {
          calcTop = topForTop;
        }
      } else if (vPlacement === 'top') {
        calcTop = topForTop;

        if (calcTop < 0 && topForBottom + contentHeight < this.h) {
          calcTop = topForBottom;
        }
      } else {
         calcTop = topForCenter;
         if (variant === 'start') calcTop = top;
         if (variant === 'end') calcTop = top + height - contentHeight;
      }

      const leftForRight = left + width;
      const leftForLeft = left - contentWidth;
      const leftForCenter = left + width / 2 - contentWidth / 2; // Aligns centers

      if (hPlacement === 'right') {
        calcLeft = leftForRight;
        // Check for horizontal collision: Flip to 'left' if it fits
        if (calcLeft + contentWidth > this.w && leftForLeft > 0) {
          calcLeft = leftForLeft;
        }
      } else if (hPlacement === 'left') {
        calcLeft = leftForLeft;
        // Check for horizontal collision: Flip to 'right' if it fits
        if (calcLeft < 0 && leftForRight + contentWidth < this.w) {
          calcLeft = leftForRight;
        }
      } else {
        calcLeft = leftForCenter;
        if (variant === 'start') calcLeft = left;
        if (variant === 'end') calcLeft = left + width - contentWidth;
      }

      const buffer = 5; // 5px buffer from window edge

      if (calcTop < buffer) {
        calcTop = buffer;
      } else if (calcTop + contentHeight > this.h - buffer) {
        calcTop = this.h - contentHeight - buffer;
      }

      // Clamp Left/Right
      if (calcLeft < buffer) {
        calcLeft = buffer;
      } else if (calcLeft + contentWidth > this.w - buffer) {
        calcLeft = this.w - contentWidth - buffer;
      }

      return {
        top: calcTop + 'px',
        left: calcLeft + 'px'
      };
    },
    // eslint-disable-next-line no-unused-vars
    getLeft ({ height, top, left, width, contentHeight, contentWidth }) {
      let leftMap = {
        'top': Number(left - Math.abs(contentWidth - width) / 2) + 'px',
        'top-start': left + 'px',
        'top-end': Number(left + width - contentWidth) + 'px',
        'bottom': Number(left - Math.abs(contentWidth - width) / 2) + 'px',
        'bottom-start': left + 'px',
        'bottom-end': Number(left + width - contentWidth) + 'px',
        "right": Number(left + width) + 'px',
        "right-start": Number(left + width) + 'px',
        "right-end": Number(left + width) + 'px',
        "left": Number(left - contentWidth) + 'px',
        "left-start": Number(left - contentWidth) + 'px',
        "left-end": Number(left - contentWidth) + 'px',
      }
      return leftMap[this.placement] || 'initial'
    },
    // eslint-disable-next-line no-unused-vars
    getTop ({ height, top, left, width, contentHeight, contentWidth }) {
      let topMap = {
        'bottom': Number(top + height) + 'px',
        'bottom-end': Number(top + height) + 'px',
        'bottom-start': Number(top + height) + 'px',
        "right": Number(top - Math.abs(height - contentHeight) /2 ) + 'px',
        "right-start": top + 'px',
        "right-end": Number(top + height - contentHeight) + 'px',
        'top': Number(top - contentHeight) + 'px',
        'top-start': Number(top - contentHeight) + 'px',
        'top-end': Number(top - contentHeight) + 'px',
        "left":  Number(top - Math.abs(height - contentHeight) /2 ) + 'px',
        "left-start": top + 'px',
        "left-end": Number(top + height - contentHeight) + 'px',
      }
      return topMap[this.placement] || 'initial'
    },
    getScrollParentDom(dom) {
        let parent = dom.parentNode || dom.parentElement;

        if (!parent || parent === document.documentElement || parent === document.body) {
            return document.body;
        }

        if (parent.nodeType !== 1) {
            return this.getScrollParentDom(parent);
        }

        if (parent.style) {
            if (
            ["scroll", "auto"].includes(parent.style.overflow) ||
            ["scroll", "auto"].includes(parent.style.overflowY) ||
            ["scroll", "auto"].includes(parent.style.overflowX)
            ) {
                return parent;
            }
        }

        try {
            const computedStyle = window.getComputedStyle(parent);
            if (
            ["scroll", "auto"].includes(computedStyle.overflow) ||
            ["scroll", "auto"].includes(computedStyle.overflowY) ||
            ["scroll", "auto"].includes(computedStyle.overflowX)
            ) {
            return parent;
            }
        } catch (e) {
            return this.getScrollParentDom(parent);
        }

        return this.getScrollParentDom(parent);
    },
    watchScroll() {
      if (this.$refs.wrap && this.$refs.wrap.firstChild) {
        let scrollParent = this.getScrollParentDom(this.$refs.wrap.firstChild);
        scrollParent.addEventListener("scroll", this.updateClickOrHoverPosition);
      }
    },
    async updateClickOrHoverPosition() {
      this.popoverStyle = await this.getHoverOrClickStyle();
    },
  },
};
</script>

<style scoped>
.container {
  display: flex;
}
.popover {
  position: fixed;
  box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);
  background-color: #fff;
  width: 200px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  padding: 12px;
  z-index: 9999000;
  color: #606266;
  /* height: 500px; */
}
</style>